import React, { useEffect, useState } from 'react';
import { rateSentence } from '../shared/speech.mjs';
import { banks } from '../lib/voiceApi';

const KREYOL_MONTHS = ['janvye', 'fevriye', 'mas', 'avril', 'me', 'jen', 'jiyè', 'out', 'septanm', 'oktòb', 'novanm', 'desanm'];

/** "2026-10-07" as a date in the interface language: "7 oktòb 2026", "7 October 2026", "7 octobre 2026". */
function sayDate(iso, lang) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map(Number);
  if (lang === 'ht') return `${d} ${KREYOL_MONTHS[m - 1]} ${y}`;
  return new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}

const figure = (n) => (n == null ? '—' : n.toFixed(2));

let last = null;   // the table, kept for the session: the server reads the BRH's page, not the app

/**
 * The Echanj screen: what the banks give for a dollar today, as the BRH
 * publishes it, under the reference rate said in Kreyòl.
 */
export default function ExchangeSheet({ lang, playing, canHear, onHear, onStop, onClose, t }) {
  const [table, setTable] = useState(last);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    if (last) return undefined;
    let mounted = true;
    banks().then((data) => { last = data; if (mounted) setTable(data); }).catch(() => { if (mounted) setFailed(true); });
    return () => { mounted = false; };
  }, []);

  const rows = table ? [
    ...table.banks.map((b) => ({ ...b, key: b.name })),
    table.highest && { key: 'highest', name: t('exchange.highest'), ...table.highest, summary: true },
    table.lowest && { key: 'lowest', name: t('exchange.lowest'), ...table.lowest, summary: true },
  ].filter(Boolean) : [];

  return (
    <>
      <div className="settings-scrim" onClick={onClose} />
      <div className="settings-sheet exchange" role="dialog" aria-label={t('exchange.title')}>
        <div className="settings-head">
          <button className="settings-close" type="button" onClick={onClose}
            aria-label={t('settings.close')} title={t('settings.close')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>
          <span className="settings-title">{t('exchange.title')}</span>
        </div>

        <div className="settings-list exchange-body">
          {!table && !failed && <p className="exchange-note">{t('exchange.loading')}</p>}
          {!table && failed && <p className="exchange-note exchange-note--error">{t('exchange.error')}</p>}
          {table && (
            <>
              <p className="exchange-date"><span>{t('exchange.date')}</span> {sayDate(table.date, lang)}</p>

              {/* The reference rate, said in Kreyòl, with the voice to hear it. */}
              <div className="answer exchange-reference" data-playing={playing || undefined}>
                <p className="answer-text">{rateSentence(table.reference, { figure: true })}</p>
                <div className="answer-actions">
                  <button className="answer-btn" type="button" onClick={() => onHear(table.reference)} disabled={!canHear || playing}
                    aria-label={t('dock.hear')} title={t('dock.hear')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M7 4.5v15l12-7.5-12-7.5z" />
                    </svg>
                  </button>
                  <button className="answer-btn" type="button" onClick={onStop} disabled={!playing}
                    aria-label={t('dock.stop')} title={t('dock.stop')}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="5.5" y="5.5" width="13" height="13" rx="1.5" fill="currentColor" />
                    </svg>
                  </button>
                  <span className="answer-speaker">BRH</span>
                </div>
              </div>

              <table className="exchange-table">
                <thead>
                  <tr><th>{t('exchange.bank')}</th><th>{t('exchange.buy')}</th><th>{t('exchange.sell')}</th></tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.key} className={row.summary ? 'exchange-summary' : undefined}>
                      <td>{row.name}</td><td>{figure(row.buy)}</td><td>{figure(row.sell)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="exchange-source">
                <a href="https://www.brh.ht/taux-affiches-par-les-banques-et-les-agents-de-change-2/" target="_blank" rel="noopener noreferrer">
                  {t('exchange.source')}
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </>
  );
}
